import { useCallback } from 'react';

import { useMemberId } from '@trello/authentication';
import {
  isAdminOfOrganization,
  isMemberOfOrganization,
} from '@trello/business-logic/organization';
import { idCache } from '@trello/id-cache';
import { useSharedStateSelector } from '@trello/shared-state';
import { workspaceState } from '@trello/workspace-state';

import { useSocketSubscription } from 'app/scripts/init/useSocketSubscription';
import type { WorkspacePageQuery } from './WorkspacePageQuery.generated';
import { useWorkspacePageQuery } from './WorkspacePageQuery.generated';

export interface WorkspacePageOrganizationQueryResult {
  organization: NonNullable<WorkspacePageQuery['organization']>;
  member: NonNullable<WorkspacePageQuery['member']>;
  isOrganizationAdmin: boolean;
  hasAccessToOrganization: boolean;
}

/**
 * Fetch the organization for the workspace page by it's id or by
 * it's name.
 */
export const useWorkspacePageOrganizationQuery = ({
  orgNameOrId,
  onCompleted,
  onError,
}: {
  orgNameOrId: string;
  onCompleted?: (data: WorkspacePageOrganizationQueryResult) => void;
  onError?: (error: Error) => void;
}) => {
  const memberId = useMemberId();
  const onCompletedCallback = useCallback(
    (data: WorkspacePageQuery) => {
      const organization: NonNullable<WorkspacePageQuery['organization']> =
        data.organization!;
      const member: NonNullable<WorkspacePageQuery['member']> = data.member!;

      const isOrganizationAdmin =
        member && organization
          ? isAdminOfOrganization(
              member,
              organization,
              organization?.enterprise,
            )
          : false;

      const isOrganizationMember =
        member && organization
          ? isMemberOfOrganization(
              member,
              organization,
              organization?.enterprise,
            )
          : false;

      const isPublicOrganization =
        organization?.prefs?.permissionLevel === 'public';

      const hasAccessToOrganization =
        !!organization && (isOrganizationMember || isPublicOrganization);

      onCompleted?.({
        organization,
        member,
        isOrganizationAdmin,
        hasAccessToOrganization,
      });
    },
    [onCompleted],
  );

  const { data, loading, error } = useWorkspacePageQuery({
    variables: {
      memberId,
      idOrName: idCache.getWorkspaceId(orgNameOrId) ?? orgNameOrId,
    },
    waitOn: ['MemberHeader', 'MemberBoards'],
    onCompleted: onCompletedCallback,
    onError,
  });

  const { member, organization } = data ?? {};
  const isOrganizationAdmin =
    member && organization
      ? isAdminOfOrganization(member, organization, organization?.enterprise)
      : false;

  const isOrganizationMember =
    member && organization
      ? isMemberOfOrganization(member, organization, organization?.enterprise)
      : false;

  const isPublicOrganization =
    organization?.prefs?.permissionLevel === 'public';

  const hasAccessToOrganization =
    !!organization && (isOrganizationMember || isPublicOrganization);

  useSocketSubscription(
    'Organization',
    organization?.id,
    !hasAccessToOrganization,
  );

  const latestGlobalOrganizationId = useSharedStateSelector(
    workspaceState,
    ({ workspaceId }) => workspaceId,
  );

  const organizationId =
    organization?.id ||
    latestGlobalOrganizationId ||
    idCache.getWorkspaceId(orgNameOrId) ||
    orgNameOrId;

  return {
    member,
    loading,
    error,
    organization,
    /**
     * The last known organization id. On initial load, it will probably first be
     * populated by the idCache, then by the shared state, and finally by the result
     * of the organization query. When toggling between organizations, the shared
     * state will hold the last known organization id until the new query completes.
     */
    organizationId,
    hasAccessToOrganization,
    isOrganizationAdmin,
    isOrganizationMember,
    isPublicOrganization,
  };
};
